import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card, Button, Tag, Switch, Input, Space, Modal,
  Form, Select, message, Typography, Popconfirm, Tooltip, Divider,
} from "antd";
import {
  EyeOutlined, EyeInvisibleOutlined, PlusOutlined, DeleteOutlined,
  CheckOutlined, CloseOutlined, DatabaseOutlined, LockOutlined,
  DragOutlined, HolderOutlined,
} from "@ant-design/icons";
import {
  fetchFieldConfigs, updateFieldConfig, createFieldConfig,
  deleteFieldConfig, seedFieldConfigs, fetchSectionConfigs, updateSectionConfig,
  batchUpdateFieldConfigs,
} from "../../utils/cifAdminApi";

const { Text } = Typography;

const SECTION_OPTIONS = [
  { value: "background",  label: "Background" },
  { value: "geography",   label: "Geography & Climate" },
  { value: "demography",  label: "Society & Demographics" },
  { value: "economy",     label: "Economy & Sectors" },
  { value: "politics",    label: "Politics" },
  { value: "partnership", label: "Partnership" },
  { value: "logistics",   label: "Logistics" },
  { value: "links",       label: "Special Links" },
];

const SECTION_COLORS = {
  background: "#123A78",
  geography: "#1A4A8C",
  demography: "#2260A8",
  economy: "#2E6DB4",
  politics: "#123A78",
  partnership: "#1A4A8C",
  logistics: "#2260A8",
  links: "#2E6DB4",
};

export default function CifFieldManager({ onFieldConfigChange }) {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [editLabel, setEditLabel] = useState({});
  const [dragId, setDragId] = useState(null);
  const [dropSection, setDropSection] = useState(null);
  const [form] = Form.useForm();

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchFieldConfigs();
      setConfigs(data);
    } catch {
      message.error("Failed to load field configs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const handleSeed = useCallback(async () => {
    setSeeding(true);
    try {
      await seedFieldConfigs();
      message.success("Field configs seeded successfully");
      await loadConfigs();
      onFieldConfigChange?.();
    } catch {
      message.error("Failed to seed field configs");
    } finally {
      setSeeding(false);
    }
  }, [loadConfigs, onFieldConfigChange]);

  const handleTogglePublic = useCallback(async (record) => {
    try {
      await updateFieldConfig(record.id, { is_public: !record.is_public });
      setConfigs((prev) =>
        prev.map((c) =>
          c.id === record.id ? { ...c, is_public: !c.is_public } : c
        )
      );
      onFieldConfigChange?.();
    } catch {
      message.error("Failed to update");
    }
  }, [onFieldConfigChange]);

  const handleSaveLabel = useCallback(async (record) => {
    const newLabel = editLabel[record.id];
    if (!newLabel || newLabel.trim() === "") return;
    try {
      await updateFieldConfig(record.id, { display_label: newLabel.trim() });
      setConfigs((prev) =>
        prev.map((c) =>
          c.id === record.id ? { ...c, display_label: newLabel.trim() } : c
        )
      );
      setEditLabel((prev) => {
        const next = { ...prev };
        delete next[record.id];
        return next;
      });
      message.success("Label updated");
      onFieldConfigChange?.();
    } catch {
      message.error("Failed to update label");
    }
  }, [editLabel, onFieldConfigChange]);

  const handleDelete = useCallback(async (id) => {
    try {
      await deleteFieldConfig(id);
      setConfigs((prev) => prev.filter((c) => c.id !== id));
      message.success("Field config deleted");
      onFieldConfigChange?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to delete";
      message.error(detail);
    }
  }, [onFieldConfigChange]);

  const handleAdd = useCallback(async () => {
    try {
      const values = await form.validateFields();
      await createFieldConfig(values);
      message.success("Field added");
      setAddModal(false);
      form.resetFields();
      await loadConfigs();
      onFieldConfigChange?.();
    } catch {
      // validation or API error
    }
  }, [form, loadConfigs, onFieldConfigChange]);

  // ─── Drag & Drop Reorder ────────────────────────────────────────────────

  const handleDragStart = useCallback((field) => {
    setDragId(field.id);
  }, []);

  const handleDragOver = useCallback((e, sectionKey) => {
    e.preventDefault();
    setDropSection(sectionKey);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDropSection(null);
  }, []);

  const handleDrop = useCallback(async (targetId) => {
    setDropSection(null);
    if (dragId === null || dragId === targetId) {
      setDragId(null);
      return;
    }

    // Find source and target fields
    const sourceIdx = configs.findIndex((c) => c.id === dragId);
    const targetIdx = configs.findIndex((c) => c.id === targetId);
    if (sourceIdx === -1 || targetIdx === -1) {
      setDragId(null);
      return;
    }

    const source = configs[sourceIdx];
    const target = configs[targetIdx];
    if (source.section_key !== target.section_key) {
      setDragId(null);
      message.warning("Cannot drag fields across different sections");
      return;
    }

    // Reorder the array
    const reordered = [...configs];
    reordered.splice(sourceIdx, 1);
    const newTargetIdx = reordered.findIndex((c) => c.id === targetId);
    reordered.splice(newTargetIdx, 0, source);

    // Re-number display_order for this section
    const sectionFields = reordered.filter((c) => c.section_key === source.section_key);
    const updates = sectionFields.map((c, i) => ({
      id: c.id,
      display_order: i,
    }));

    try {
      await batchUpdateFieldConfigs(updates);
      setConfigs(reordered.map((c, i) => {
        const update = updates.find((u) => u.id === c.id);
        return update ? { ...c, display_order: update.display_order } : c;
      }));
      onFieldConfigChange?.();
    } catch {
      message.error("Failed to reorder fields");
    } finally {
      setDragId(null);
    }
  }, [dragId, configs, onFieldConfigChange]);

  const groupedConfigs = useMemo(() => {
    const groups = {};
    for (const c of configs) {
      if (!groups[c.section_key]) groups[c.section_key] = [];
      groups[c.section_key].push(c);
    }
    return groups;
  }, [configs]);

  if (!loading && configs.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: 60 }}>
        <DatabaseOutlined style={{ fontSize: 48, color: "#bbb", display: "block", marginBottom: 16 }} />
        <Text type="secondary" style={{ display: "block", marginBottom: 16, fontSize: 14 }}>
          No field configuration found. Click below to initialize with default fields.
        </Text>
        <Button
          type="primary"
          icon={<DatabaseOutlined />}
          onClick={handleSeed}
          loading={seeding}
        >
          Initialize Field Configs
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <Space>
          <Text strong style={{ fontSize: 13 }}>
            Total {configs.length} fields across {Object.keys(groupedConfigs).length} sections
          </Text>
        </Space>
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setAddModal(true)}>
          Add Custom Field
        </Button>
      </div>

      {Object.entries(groupedConfigs).map(([sectionKey, fields]) => (
        <Card
          key={sectionKey}
          size="small"
          title={
            <Space>
              <Tag color={SECTION_COLORS[sectionKey] || "#666"}>
                {SECTION_OPTIONS.find((s) => s.value === sectionKey)?.label || sectionKey}
              </Tag>
              <Text style={{ fontSize: 12, color: "#999" }}>{fields.length} fields</Text>
            </Space>
          }
          style={{ marginBottom: 12, borderRadius: 8 }}
          styles={{ body: { padding: 0 } }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#fafafa" }}>
                <th style={{ ...thStyle, textAlign: "center", width: 36 }}></th>
                <th style={thStyle}>Field Name</th>
                <th style={thStyle}>Display Label</th>
                <th style={thStyle}>Type</th>
                <th style={{ ...thStyle, textAlign: "center", width: 80 }}>Public</th>
                <th style={{ ...thStyle, textAlign: "center", width: 60 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field) => (
                <tr
                  key={field.id}
                  draggable
                  onDragStart={() => handleDragStart(field)}
                  onDragOver={(e) => handleDragOver(e, sectionKey)}
                  onDragLeave={handleDragLeave}
                  onDrop={() => handleDrop(field.id)}
                  style={{
                    borderBottom: "1px solid #f0f0f0",
                    cursor: "grab",
                    opacity: dragId === field.id ? 0.4 : 1,
                    background: dropSection === sectionKey && dragId !== field.id
                      ? "#eef3fa" : undefined,
                    transition: "background 0.15s, opacity 0.15s",
                  }}
                >
                  <td style={{ ...tdStyle, textAlign: "center", cursor: "grab", color: "#bbb" }}>
                    <HolderOutlined />
                  </td>
                  <td style={tdStyle}>
                    <Space>
                      <Text code style={{ fontSize: 12 }}>{field.field_name}</Text>
                      {field.is_db_field && (
                        <Tooltip title="Database field — cannot be deleted">
                          <DatabaseOutlined style={{ color: "#999", fontSize: 12 }} />
                        </Tooltip>
                      )}
                    </Space>
                  </td>
                  <td style={tdStyle}>
                    {editLabel[field.id] !== undefined ? (
                      <Space.Compact style={{ width: "100%" }}>
                        <Input
                          size="small"
                          value={editLabel[field.id]}
                          onChange={(e) =>
                            setEditLabel((prev) => ({ ...prev, [field.id]: e.target.value }))
                          }
                          onPressEnter={() => handleSaveLabel(field)}
                          style={{ width: 140 }}
                        />
                        <Button size="small" icon={<CheckOutlined />} onClick={() => handleSaveLabel(field)} />
                        <Button
                          size="small"
                          icon={<CloseOutlined />}
                          onClick={() =>
                            setEditLabel((prev) => {
                              const next = { ...prev };
                              delete next[field.id];
                              return next;
                            })
                          }
                        />
                      </Space.Compact>
                    ) : (
                      <Text
                        style={{ fontSize: 13, cursor: "pointer" }}
                        onClick={() =>
                          setEditLabel((prev) => ({ ...prev, [field.id]: field.display_label }))
                        }
                      >
                        {field.display_label}
                      </Text>
                    )}
                  </td>
                  <td style={tdStyle}>
                    <Tag style={{ fontSize: 11 }}>{field.field_type}</Tag>
                  </td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>
                    <Tooltip title={field.is_public ? "Visible on profile" : "Hidden from profile"}>
                      <Switch
                        size="small"
                        checked={field.is_public}
                        onChange={() => handleTogglePublic(field)}
                        checkedChildren={<EyeOutlined />}
                        unCheckedChildren={<EyeInvisibleOutlined />}
                      />
                    </Tooltip>
                  </td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>
                    {field.is_db_field ? (
                      <Tooltip title="Database field — use Public toggle to show/hide">
                        <Button size="small" disabled icon={<LockOutlined />} />
                      </Tooltip>
                    ) : (
                      <Popconfirm
                        title="Delete this custom field?"
                        onConfirm={() => handleDelete(field.id)}
                        okText="Delete"
                        okType="danger"
                      >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ))}

      <Divider style={{ fontSize: 12, color: "#999" }}>Section Visibility Settings</Divider>
      <SectionVisibilityManager />

      <Modal
        title="Add Custom Field"
        open={addModal}
        onCancel={() => { setAddModal(false); form.resetFields(); }}
        onOk={handleAdd}
        okText="Add Field"
      >
        <Form form={form} layout="vertical" size="small">
          <Form.Item name="section_key" label="Section" rules={[{ required: true, message: "Select section" }]}>
            <Select options={SECTION_OPTIONS} placeholder="Select section" />
          </Form.Item>
          <Form.Item
            name="field_name"
            label="Field Name (snake_case)"
            rules={[
              { required: true, message: "Enter field name" },
              { pattern: /^[a-z_][a-z0-9_]*$/, message: "Use snake_case (a-z, 0-9, _)" },
            ]}
          >
            <Input placeholder="e.g. gini_coefficient" />
          </Form.Item>
          <Form.Item name="display_label" label="Display Label" rules={[{ required: true, message: "Enter display label" }]}>
            <Input placeholder="e.g. Gini Coefficient" />
          </Form.Item>
          <Form.Item name="field_type" label="Field Type" initialValue="text">
            <Select
              options={[
                { value: "text", label: "Text (single line)" },
                { value: "textarea", label: "Textarea (multi line)" },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

const thStyle = {
  padding: "6px 10px",
  fontSize: 11,
  fontWeight: 600,
  color: "#666",
  textTransform: "uppercase",
  borderBottom: "2px solid #e8e8e8",
};

const tdStyle = {
  padding: "6px 10px",
  fontSize: 13,
};

function SectionVisibilityManager() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSectionConfigs();
      setSections(data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleTogglePublic = useCallback(async (sec) => {
    try {
      await updateSectionConfig(sec.section_key, { is_public: !sec.is_public });
      setSections((prev) =>
        prev.map((s) =>
          s.section_key === sec.section_key ? { ...s, is_public: !s.is_public } : s
        )
      );
    } catch {
      message.error("Failed to update section visibility");
    }
  }, []);

  const handleToggleActive = useCallback(async (sec) => {
    try {
      await updateSectionConfig(sec.section_key, { is_active: !sec.is_active });
      setSections((prev) =>
        prev.map((s) =>
          s.section_key === sec.section_key ? { ...s, is_active: !s.is_active } : s
        )
      );
    } catch {
      message.error("Failed to update section status");
    }
  }, []);

  if (loading || sections.length === 0) return null;

  return (
    <div style={{ marginTop: 8 }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "#fafafa" }}>
            <th style={thStyle}>Section</th>
            <th style={{ ...thStyle, textAlign: "center", width: 90 }}>Public</th>
            <th style={{ ...thStyle, textAlign: "center", width: 90 }}>Active</th>
            <th style={{ ...thStyle, textAlign: "center", width: 60 }}>Order</th>
          </tr>
        </thead>
        <tbody>
          {sections.map((sec) => (
            <tr key={sec.section_key} style={{ borderBottom: "1px solid #f0f0f0" }}>
              <td style={tdStyle}>
                <Text strong style={{ fontSize: 13 }}>{sec.display_name}</Text>
              </td>
              <td style={{ ...tdStyle, textAlign: "center" }}>
                <Tooltip title={sec.is_public ? "Section is public" : "Section is private"}>
                  <Switch
                    size="small"
                    checked={sec.is_public}
                    onChange={() => handleTogglePublic(sec)}
                    checkedChildren={<EyeOutlined />}
                    unCheckedChildren={<EyeInvisibleOutlined />}
                  />
                </Tooltip>
              </td>
              <td style={{ ...tdStyle, textAlign: "center" }}>
                <Switch
                  size="small"
                  checked={sec.is_active}
                  onChange={() => handleToggleActive(sec)}
                />
              </td>
              <td style={{ ...tdStyle, textAlign: "center", fontSize: 12, color: "#999" }}>
                {sec.display_order}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}